import { useCallback, useMemo } from 'react';
import type { AttributeTreeNode } from '../state/types';
import { TEMPLATE_SECTION_KEY } from '../utils/templateCapture';

type PromptSectionOrder = {
  key: string;
  display_order?: number;
  effective_order?: number;
};

/**
 * 属性ツリーを UI 表示用に整形するフック。
 *
 * GenerationWorkspace から切り出したもので、ロジックは移設のみ (挙動不変)。
 * 提供するもの:
 * - `orderedAttributeTree`: 仮想 root (composition / style) を平坦化し、
 *   セクション順 → sort_order で並べたフラットな top group 配列
 * - `sectionDisplayOrder` / `accordionOrderFor`: アコーディオンの CSS order
 * - `hasComposition` / `hasPerson`: ビルトイン group の有効判定
 * - `extraTopLevelGroups`: OEM が追加した top group (+ order)
 * - `templateGroups` / `visibleTemplateGroups`: テンプレートモード用の group
 */
export function useOrderedAttributeTree(
  attributeTree: AttributeTreeNode[],
  sections: PromptSectionOrder[] | undefined,
) {
  const orderedAttributeTree = useMemo(() => {
    // composition / style は画面上のグルーピング用の仮想 root。
    // 生成ロジックや UI は background / scene / ... といった属性キーを
    // 直接参照するため、composition / style 等のセクション直下 group は root レベルに平坦化する。
    // ラベルは DB の label（OEM 組織は organization_attribute_overrides で上書き済み）をそのまま使う。
    // 並び順はバックエンドの prompt_sections.effective_order (OEM 上書き反映済み) に従い、
    // セクション内では各サブ group の sort_order を尊重する。
    const sectionList = sections ?? [];
    const sectionOrderMap = new Map(
      sectionList.map((s) => [s.key, s.effective_order ?? s.display_order ?? 0]),
    );

    type Annotated = AttributeTreeNode & { __sectionOrder: number; __subOrder: number };
    const annotated: Annotated[] = [];
    const VIRTUAL_GROUP_KEYS = new Set(['composition', 'style']);
    for (const node of attributeTree) {
      const nodeSection = node.section ?? null;
      const sectionOrder = nodeSection ? (sectionOrderMap.get(nodeSection) ?? 999) : 999;
      if (VIRTUAL_GROUP_KEYS.has(node.key)) {
        // composition / style は UI 上 "仮想グループ" として平坦化: 配下を section 付きで展開
        for (const child of node.children ?? []) {
          annotated.push({
            ...child,
            __section: nodeSection,
            __sectionOrder: sectionOrder,
            __subOrder: child.sort_order ?? 0,
          });
        }
      } else {
        // person のようにネスト維持する top group、および OEM が追加した独自 top group
        annotated.push({
          ...node,
          __section: nodeSection,
          __sectionOrder: sectionOrder,
          __subOrder: node.sort_order ?? 0,
        });
      }
    }

    annotated.sort((a, b) => {
      if (a.__sectionOrder !== b.__sectionOrder) return a.__sectionOrder - b.__sectionOrder;
      return a.__subOrder - b.__subOrder;
    });

    return annotated.map(({ __sectionOrder, __subOrder, ...rest }) => rest as AttributeTreeNode);
  }, [attributeTree, sections]);

  // セクションアコーディオンの表示順 (OEM 上書きを反映)。
  // 'composition' (背景・構図) / 'person' (人物) / 'style' (スタイル) のみ動的順序。
  // テキスト等の固定セクションは大きな order 値を直接指定して最後に並べる。
  const sectionDisplayOrder = useMemo<Record<string, number>>(() => {
    // テンプレートは背景・構図 (10) の直下に置く。
    const out: Record<string, number> = { composition: 10, [TEMPLATE_SECTION_KEY]: 15, person: 20, style: 30 };
    for (const s of sections ?? []) {
      if (s.key in out) {
        out[s.key] = s.effective_order ?? s.display_order ?? out[s.key];
      }
    }
    return out;
  }, [sections]);

  // CSS order はセクション順 x 1000 + ノードの sort_order でソートする。
  // これにより、section='composition' の OEM 追加 group は背景・構図アコーディオン
  // (order=10000) の直後、人物 (20000) より前に並ぶ。
  const ACCORDION_ORDER_BASE = 1000;
  const accordionOrderFor = useCallback(
    (sectionKey: string | null | undefined, sortOrder: number) => {
      const so = sectionKey ? (sectionDisplayOrder[sectionKey] ?? 50) : 50;
      return so * ACCORDION_ORDER_BASE + sortOrder;
    },
    [sectionDisplayOrder],
  );

  // ビルトイン 3 group の有効判定。OEM が共通ノードを非表示設定にすると
  // /generation/attributes のツリーから除外されるため、tree に居なければ非表示扱い。
  const hasBuiltinGroup = (key: string) => (attributeTree ?? []).some((n) => n.key === key);
  const hasComposition = hasBuiltinGroup('composition');
  const hasPerson = hasBuiltinGroup('person');

  // OEM が追加したトップレベル group。既存の 3 つのビルトイン group と
  // テンプレート group (専用アコーディオンで描画) は除外。
  const extraTopLevelGroups = useMemo(() => {
    const BUILTIN = new Set(['composition', 'style', 'person']);
    return (attributeTree ?? [])
      .filter((n) => n.type === 'group' && !BUILTIN.has(n.key) && n.section !== TEMPLATE_SECTION_KEY)
      .map((n) => ({
        node: n,
        order: accordionOrderFor(n.section ?? null, n.sort_order ?? 0),
      }));
  }, [attributeTree, accordionOrderFor]);

  // テンプレートモード: section='template' のトップレベル group。
  // 運用上は 1 つを想定するが、複数存在しても壊れないよう配列で扱う。
  const templateGroups = useMemo(
    () => (attributeTree ?? []).filter((n) => n.type === 'group' && n.section === TEMPLATE_SECTION_KEY),
    [attributeTree],
  );

  // 通常のテンプレートアコーディオンに表示する group。metadata.purpose_only===true
  // (例: バナーテンプレ群 banner_ad。P3「実装時の確定事項」) は目的軸モード専用のため除外する。
  // activeTemplate 解決・selectTemplateOption・テンプレートロックは templateGroups (全件) を
  // 引き続き対象にする (目的軸モードでバナー選択した際もロックが効く必要があるため)。
  const visibleTemplateGroups = useMemo(
    () => templateGroups.filter((g) => (g.metadata as { purpose_only?: boolean } | null | undefined)?.purpose_only !== true),
    [templateGroups],
  );

  return {
    orderedAttributeTree,
    sectionDisplayOrder,
    accordionOrderFor,
    hasComposition,
    hasPerson,
    extraTopLevelGroups,
    templateGroups,
    visibleTemplateGroups,
  };
}
